import {
  InfoOutlined,
  LabelOutlined,
  NumbersOutlined,
  PublicOutlined,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  FormControl,
  Skeleton,
  SxProps,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import { ReactNode, useEffect } from "react";
import { Controller } from "react-hook-form";
import { Port } from "../../../types/port";
import { useStoreForm } from "../form/usePortForm";
import { PortStatus } from "../status";

type Props = {
  port?: Port;
  isEditing: boolean;
  form: ReturnType<typeof useStoreForm>;
};

type FieldName = "name" | "code" | "countryCode";

export const PortInfoCard = ({ port, isEditing, form }: Props) => {
  const {
    control,
    resetForm,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (!isEditing) {
      resetForm();
    }
  }, [isEditing]);

  const renderField = (name: FieldName, label: string, icon: ReactNode) => {
    const fieldRules: Record<FieldName, any> = {
      name: { required: "Name is required" },
      code: { required: "Code is required" },
      countryCode: {
        required: "Country code is required",
        pattern: {
          value: /^[A-Z]{3}$/,
          message: "Must be a valid 3-letter ISO code",
        },
      },
    };

    return (
      <>
        <Info
          icon={icon}
          label={label}
          value={
            <FormControl fullWidth error={!!errors[name]}>
              {isEditing ? (
                <Controller
                  name={name}
                  control={control}
                  rules={fieldRules[name]}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      error={!!errors[name]}
                      helperText={errors[name]?.message as string}
                    />
                  )}
                />
              ) : (
                port?.[name] ?? "-"
              )}
            </FormControl>
          }
        />
      </>
    );
  };

  return (
    <Box>
      {renderField("name", "Name", <LabelOutlined />)}
      <Divider />
      {renderField("code", "Code", <NumbersOutlined />)}
      <Divider />
      {renderField("countryCode", "Country code", <PublicOutlined />)}
    </Box>
  );
};

export const PortStatusCard = ({ port, isEditing, form }: Props) => {
  const {
    control,
    resetForm,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (!isEditing) {
      resetForm();
    }
  }, [isEditing]);

  return (
    <Box>
      <Info
        icon={<InfoOutlined />}
        label={"Status"}
        value={
          <FormControl>
            {isEditing ? (
              <Controller
                name="isDeleted"
                defaultValue={port?.isDeleted ?? false}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <ToggleButtonGroup
                    value={value}
                    exclusive
                    size="small"
                    color="primary"
                    onChange={(_, newValue) => {
                      if (newValue !== null) {
                        onChange(newValue);
                      }
                    }}
                  >
                    <ToggleButton value={false}>Active</ToggleButton>
                    <ToggleButton value={true}>Deleted</ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            ) : (
              <PortStatus port={port} />
            )}
          </FormControl>
        }
      />
    </Box>
  );
};

type InfoProps = {
  icon: ReactNode;
  label: string;
  value?: ReactNode;
  sx?: SxProps;
};

export const Info = ({ icon, label, value, sx }: InfoProps) => {
  const { palette } = useTheme();

  return (
    <Box
      sx={[
        {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          p: "16px 0px 16px 24px",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        sx={{
          mr: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: palette.primary.main,
        }}
      >
        {icon}
      </Box>
      <Box
        sx={{
          mr: "8px",
          display: "flex",
          alignItems: "center",
          width: "112px",
          height: "100%",
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        {value ?? (
          <Skeleton variant="text" sx={{ fontSize: "1rem", width: "200px" }} />
        )}
      </Box>
    </Box>
  );
};
